import { Component, OnInit, Input } from '@angular/core';
import { Regex } from '@pg-app/shared/constants/regex';
import { FormGroup, Validators } from '@angular/forms';
import { RemitterTypeEnum, RemitterTypes } from '@pg-app/client/payment/constants/remitter-type';
import { SendPaymentInstructionService } from '@pg-app/client/send-payment-instruction/services/send-payment-instruction.service';
import { UltimateRemitterSourceEnum } from '@pg-app/shared/enums/ultimate-remitter-source.enum';
import { CountriesWithMandatoryStateOrProvince } from '@nttp/constants/countries-required-state-province';

@Component({
  selector: 'np-remitter-details',
  templateUrl: './remitter-details.component.html',
  styleUrls: ['./remitter-details.component.scss']
})
export class RemitterDetailsComponent implements OnInit {
  @Input() remitterDetailsForm: FormGroup;
  @Input() countries;
  @Input() ultimateRemitterSource: UltimateRemitterSourceEnum;
  @Input() fullyStretchView: boolean;

  remitterTypes = RemitterTypes;
  ultimateRemitters = [];
  addNew = false;

  isIndividual = false;
  isCompany = false;

  constructor(
    private sendPaymentInstructionService: SendPaymentInstructionService
  ) { }

  ngOnInit() {
    if (!this.ultimateRemitterSource) {
      this.ultimateRemitterSource = UltimateRemitterSourceEnum.others;
    }

    if (this.ultimateRemitterSource === UltimateRemitterSourceEnum.updatingImportedPayment) {
      this.addNew = true;
      this.changeRemitterType();
      this.changeEditMode();
      this.changeRemitterCountry();
    } else {
      this.prepareData();
    }
  }

  private prepareData() {
    const params = {
      id: null,
      getAll: true,
    };

    this.addNew = false;

    this.sendPaymentInstructionService.getUltimateRemitters(params).subscribe(
      data => {
        this.ultimateRemitters = data;

        // This account has currently no ultimate remitters,
        // so set 'addNew' to true i.e. forcing user to add a new one
        if (!data || data.length === 0) {
          this.addNew = true;
        }

        this.changeEditMode();
      }
    )
  }

  changeRemitterCountry() {
    const remitterCountryValue = this.remitterDetailsForm.get('remitterCountry').value;

    if (CountriesWithMandatoryStateOrProvince.includes(remitterCountryValue)) {
      this.remitterDetailsForm.get('remitterState').setValidators([Validators.required, Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]);
    }
    else {
      this.remitterDetailsForm.get('remitterState').setValidators([Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]);
    }
    this.remitterDetailsForm.get('remitterState').updateValueAndValidity();
  }

  changeRemitterType() {
    const remitterTypeValue = this.remitterDetailsForm.get('remitterType').value;
    if (!remitterTypeValue) {
      this.isIndividual = false;
      this.isCompany = false;
      return;
    }

    const remitterType = RemitterTypes.find(p => p.name === remitterTypeValue).id;

    if (remitterType === RemitterTypeEnum.Company) {
      this.isIndividual = false;
      this.isCompany = true;

      // Company name and company number are required
      this.remitterDetailsForm.get('remitterCompanyName').setValidators([Validators.required, Validators.pattern(Regex.nonSwiftCharacters), Validators.maxLength(35)]);
      this.remitterDetailsForm.get('remitterCompanyNumber').setValidators([Validators.required, Validators.pattern(Regex.nonSwiftCharacters)]);

      // first name, last name and DOB are optional
      this.remitterDetailsForm.get('remitterFirstName').setValidators([Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
      this.remitterDetailsForm.get('remitterLastName').setValidators([Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
      this.remitterDetailsForm.get('remitterDOB').setValidators([]);
    } else if (remitterType === RemitterTypeEnum.Individual) {
      this.isIndividual = true;
      this.isCompany = false;

      this.remitterDetailsForm.get('remitterFirstName').setValidators([Validators.required, Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
      this.remitterDetailsForm.get('remitterLastName').setValidators([Validators.required, Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
      this.remitterDetailsForm.get('remitterDOB').setValidators([Validators.required]);

      this.remitterDetailsForm.get('remitterCompanyNumber').setValidators([Validators.pattern(Regex.nonSwiftCharacters)]);
      this.remitterDetailsForm.get('remitterCompanyName').setValidators([Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
    } else {
      this.isIndividual = false;
      this.isCompany = false;
    }

    this.remitterDetailsForm.get('remitterAddress').setValidators([Validators.required, Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
    this.remitterDetailsForm.get('remitterCity').setValidators([Validators.required, Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]);
    this.remitterDetailsForm.get('remitterPostCode').setValidators([Validators.required, Validators.maxLength(12), Validators.pattern(Regex.nonSwiftCharacters)]);
    this.remitterDetailsForm.get('remitterState').setValidators([Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]);
    this.remitterDetailsForm.get('remitterCountry').setValidators([Validators.required]);

    this.remitterDetailsForm.get('remitterCompanyName').updateValueAndValidity();
    this.remitterDetailsForm.get('remitterFirstName').updateValueAndValidity();
    this.remitterDetailsForm.get('remitterLastName').updateValueAndValidity();
    this.remitterDetailsForm.get('remitterDOB').updateValueAndValidity();
    this.remitterDetailsForm.get('remitterCompanyNumber').updateValueAndValidity();

    this.remitterDetailsForm.get('remitterAddress').updateValueAndValidity();
    this.remitterDetailsForm.get('remitterCity').updateValueAndValidity();
    this.remitterDetailsForm.get('remitterPostCode').updateValueAndValidity();
    this.remitterDetailsForm.get('remitterState').updateValueAndValidity();
    this.remitterDetailsForm.get('remitterCountry').updateValueAndValidity();
  }

  addNewUltimateRemitter() {
    this.addNew = true;

    this.remitterDetailsForm.get('ultimateRemitterSelected').setValue(null);
    this.changeEditMode();
  }

  changeEditMode() {
    if (this.addNew) {
      this.remitterDetailsForm.get('ultimateRemitterSelected').clearValidators();
      this.remitterDetailsForm.get('remitterType').setValidators([Validators.required]);
    } else {
      this.remitterDetailsForm.get('ultimateRemitterSelected').setValidators([Validators.required]);
      this.remitterDetailsForm.get('remitterType').clearValidators();
    }
    this.remitterDetailsForm.get('ultimateRemitterSelected').updateValueAndValidity();
    this.remitterDetailsForm.get('remitterType').updateValueAndValidity();
  }
}
